import { Navigate, Route, Routes } from 'react-router-dom'
import { AdminLayout } from './components/AdminLayout'
import { ReaderLayout } from './components/ReaderLayout'
import { useSession } from './lib/session'
import { LandingPage } from './pages/LandingPage'
import { RightsPage } from './pages/RightsPage'
import { LoginPage } from './pages/LoginPage'
import { SearchPage } from './pages/SearchPage'
import { BrowsePage, EditionPage } from './pages/BrowsePage'
import { ViewerPage } from './pages/ViewerPage'
import { PermalinkPage } from './pages/PermalinkPage'
import { ArticlePage } from './pages/ArticlePage'
import { UnitPage } from './pages/UnitPage'
import { ClippingsPage, SavedSearchesPage } from './pages/ClippingsPage'
import { TimelinePage } from './pages/TimelinePage'
import { ComparePage } from './pages/ComparePage'
import { AccountPage } from './pages/AccountPage'
import { ReviewPage } from './pages/ReviewPage'
import { OverviewPage } from './pages/OverviewPage'
import { UsersPage } from './pages/UsersPage'
import { RolesPage } from './pages/RolesPage'
import { FeatureFlagsPage } from './pages/FeatureFlagsPage'
import { SeatsPage } from './pages/SeatsPage'
import { IngestPage } from './pages/IngestPage'
import { AuditPage } from './pages/AuditPage'
import { LicencesPage } from './pages/LicencesPage'
import { SearchTuningPage } from './pages/SearchTuningPage'
import { UsagePage } from './pages/UsagePage'
import { Logo } from './components/Logo'

export default function App() {
  const { me, loading, can } = useSession()

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center gap-3 text-ink2">
        <Logo size={20} />
        <span className="ident uppercase tracking-[0.12em]">Loading</span>
      </div>
    )
  }

  const signedIn = (element: React.ReactNode) =>
    me ? element : <Navigate to="/login" replace />

  return (
    <Routes>
      {/*
        The landing page is public and is what `/` shows to anyone not signed
        in. A signed-in reader has no use for the pitch, so they land on the
        search page instead.
      */}
      <Route path="/" element={me ? <Navigate to="/search" replace /> : <LandingPage />} />
      <Route path="/login" element={me ? <Navigate to="/search" replace /> : <LoginPage />} />
      <Route path="/rights" element={<RightsPage />} />

      {/*
        The reader. Search and the viewer are reachable without signing in:
        that is the discovery tier, and it is enforced in the API rather than
        here, so a guest sees real results as metadata-only stubs. Everything
        that belongs to a person is behind a sign-in.
      */}
      <Route element={<ReaderLayout />}>
        <Route path="/search" element={<SearchPage />} />
        <Route path="/browse" element={<BrowsePage />} />
        <Route path="/browse/:editionId" element={<EditionPage />} />
        <Route path="/page/:pageId" element={<ViewerPage />} />
        <Route path="/p/:slug/:date/:label" element={<PermalinkPage />} />
        <Route path="/article/:articleId" element={<ArticlePage />} />
        <Route path="/unit/:unitId" element={<UnitPage />} />
        <Route path="/timeline" element={<TimelinePage />} />
        <Route path="/compare" element={<ComparePage />} />
        <Route path="/clippings" element={signedIn(<ClippingsPage />)} />
        <Route path="/clippings/:projectId" element={signedIn(<ClippingsPage />)} />
        <Route path="/saved" element={signedIn(<SavedSearchesPage />)} />
        <Route path="/account" element={signedIn(<AccountPage />)} />
        {/* The editor's queue: keyboard-first, crop left, text right. */}
        <Route
          path="/review"
          element={can('review.queue.view') ? <ReviewPage /> : <Navigate to="/search" replace />}
        />
      </Route>

      {/* The admin console. */}
      <Route element={<AdminLayout />}>
        <Route path="/admin" element={<OverviewPage />} />
        <Route path="/admin/users" element={<UsersPage />} />
        <Route path="/admin/roles" element={<RolesPage />} />
        <Route path="/admin/features" element={<FeatureFlagsPage />} />
        <Route path="/admin/seats" element={<SeatsPage />} />
        <Route path="/admin/licences" element={<LicencesPage />} />
        <Route path="/admin/ingest" element={<IngestPage />} />
        <Route path="/admin/ingest/:editionId" element={<IngestPage />} />
        <Route path="/admin/usage" element={<UsagePage />} />
        <Route path="/admin/search-tuning" element={<SearchTuningPage />} />
        <Route path="/admin/audit" element={<AuditPage />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
